import React, {Dispatch, FC, SetStateAction, useEffect, useState} from 'react';
import {clearEditEmptyLeg, editEmptyLeg,} from "../../../../../store/reducers/emptyLegsReducer/emptyLegsReducer";
import {Col, Form, message, Radio, Row} from "antd";
import {useAppDispatch, useAppSelector} from "../../../../../store/store";
import {CustomDatePicker} from "../../../../../components/UI/AntCustom/CustomDatePicker";
import {DateAndTimePicker} from "../../../../../components/UI/AntCustom/DateAndTimePicker";
import {DateAndTimeRangePicker} from "../../../../../components/UI/AntCustom/DateAndTimeRangePicker";
import {Dayjs} from "dayjs";
import {DebounceSelect} from "../../../../../components/UI/AntCustom/DebounceSelect";
import {IEmptyLeg} from "../../../../../store/reducers/emptyLegsReducer/emptyLegsReducerTypes";
import {
  fetchCurrencies,
  getInitialCurrencyForSelect
} from "../../../../../utils/helpers/selectDataFetchers/fetchCurrencies";
import {CustomFieldInput} from "../../../../../components/UI/AntCustom/CustomFieldInput";
import {CustomButton} from "../../../../../components/UI/AntCustom/CustomButton";
import {CustomModal} from "../../../../../components/UI/AntCustom/CustomModal";
import {dayjsWithOffset, dayjsToUTCDate} from "../../../../../utils/helpers/dateHelpers/dateHelpers";
import {renderSuccessModal} from "../../../../../components/UI/StatusModals/renderSuccessModal";
import {EmptyLegDateTypeEnum, useEmptyLegDateTypeName} from "../../../../../utils/constans/business/emptyLegDateType";
import {isDateLessTanOneMinuteAgo} from "../../../../../utils/helpers/dateHelpers/scheduleDateHelpers";
import {defineMessages, useIntl} from "react-intl";
import {CommonFormMessages} from "../../../../../intl/commonMessages/CommonFormMessages";
import {CommonUnitMessages} from "../../../../../intl/commonMessages/CommonUnitMessages";
import {CommonButtonMessages} from "../../../../../intl/commonMessages/CommonButtonMessages";
import {CustomInputNumber} from "../../../../../components/UI/AntCustom/CustomInputNumber";
import {CustomSwitch} from "../../../../../components/UI/AntCustom/CustomSwitch";
import {onFinishTrimmed} from "../../../../../utils/helpers/formHelpers/onFinishTrimmed";
import {convertFromKm, convertToKm, getUnitLabel} from "../../../../../utils/helpers/distanceConverter/distanceConverter";
import {DistanceUnit} from "../../../../../utils/constans/statuses/distanceUnits";
import {getDateAndTimePickerValidationRules} from "../../../../../utils/helpers/formHelpers/getDateAndTimePickerValidationRules";


const EditEmptyLegModalMessages = defineMessages({
  success: {
    id: "editEmptyLegModal.success",
    defaultMessage: "Empty Leg has been successfully edited for the aircraft",
  },
  title: {
    id: "editEmptyLegModal.title",
    defaultMessage: "Edit Empty Leg",
  },
})

interface IEditEmptyLegFormData {
  dateTypeRadio: EmptyLegDateTypeEnum;
  availableDates?: [Dayjs, Dayjs];
  departureDate?: Dayjs;
  airportFromId: number;
  airportToId: number;
  fleetId: number;
  priceAmount: number;
  priceCurrencyId: number;
  hours: number;
  minutes: number;
  distance: number;
  comment: string;
}

interface IEditEmptyLegModal {
  emptyLeg: IEmptyLeg;
  isModalVisible: boolean;
  setIsModalVisible: Dispatch<SetStateAction<boolean>>;
}

export const EditEmptyLegModal: FC<IEditEmptyLegModal> = ({emptyLeg, isModalVisible, setIsModalVisible}) => {
  const [form] = Form.useForm();
  const dispatch = useAppDispatch();
  const intl = useIntl();
  const {
    isLoading,
    isSuccess,
    errorMessage,
  } = useAppSelector(state => state.emptyLegs.edit);
  const userDistanceUnit = useAppSelector(state => state.user.oneItemObject.item.distanceUnit) || DistanceUnit.NAUTICAL_MILE;
  const [dateTypeRadioValue, setDateTypeRadioValue] = useState<EmptyLegDateTypeEnum>(
    emptyLeg.departureDate ? EmptyLegDateTypeEnum.SPECIFIC : EmptyLegDateTypeEnum.RANGE
  )
  const [isPriceOnRequest, setIsPriceOnRequest] = useState<boolean>(emptyLeg.priceOnRequest ?? false);
  const emptyLegDateTypeNameRange = useEmptyLegDateTypeName(EmptyLegDateTypeEnum.RANGE);
  const emptyLegDateTypeNameSpecific = useEmptyLegDateTypeName(EmptyLegDateTypeEnum.SPECIFIC);

  useEffect(() => {
    return () => {
      dispatch(clearEditEmptyLeg());
    }
  }, [])

  useEffect(() => {
    if (!isLoading) {
      if (isSuccess)
        renderSuccessModal(`${intl.formatMessage(EditEmptyLegModalMessages.success)} ${emptyLeg.plane.registrationNumber}`, () => setIsModalVisible(false));
      if (errorMessage)
        message.error(errorMessage);
    }
  }, [isSuccess, errorMessage, isLoading])

  const onFinish = (values: IEditEmptyLegFormData) => {
    dispatch(editEmptyLeg({
      emptyLegId: emptyLeg.id,
      availableFromDate: values.availableDates ? dayjsToUTCDate(values.availableDates[0]) : undefined,
      availableToDate: values.availableDates ? dayjsToUTCDate(values.availableDates[1]) : undefined,
      departureDate: values.departureDate ? dayjsToUTCDate(values.departureDate) : undefined,
      airportFromId: emptyLeg.airportFrom.id,
      airportToId: emptyLeg.airportTo.id,
      fleetId: emptyLeg.plane.id,
      priceAmount: isPriceOnRequest ? 0 : values.priceAmount,
      priceCurrencyId: isPriceOnRequest ? 0 : values.priceCurrencyId,
      hours: emptyLeg.flyHours,
      minutes: emptyLeg.flyMinutes,
      comment: values.comment,
      distance: convertToKm(values.distance, userDistanceUnit),
      emptyLegStatusId: emptyLeg.emptyLegStatusId,
      priceOnRequest: isPriceOnRequest,
    }));
  }

  const onFinishFailed = () => {
    message.error(intl.formatMessage(CommonFormMessages.incorrect))
  }

  const handleFormValuesChange = (changedValues: any) => {
    const fieldName = Object.keys(changedValues)[0];

    if (fieldName === "dateTypeRadio" && changedValues.hasOwnProperty(fieldName)) {
      const value = changedValues[fieldName];
      setDateTypeRadioValue(value)
    }
  }

  const initialValues: IEditEmptyLegFormData = {
    dateTypeRadio: emptyLeg.departureDate ? EmptyLegDateTypeEnum.SPECIFIC : EmptyLegDateTypeEnum.RANGE,
    availableDates: emptyLeg.availableFromDate && emptyLeg.availableToDate
      ? [dayjsWithOffset(emptyLeg.availableFromDate, emptyLeg.airportFrom.timezone.offset),
         dayjsWithOffset(emptyLeg.availableToDate, emptyLeg.airportFrom.timezone.offset)]
      : undefined,
    departureDate: emptyLeg.departureDate ? dayjsWithOffset(emptyLeg.departureDate, emptyLeg.airportFrom.timezone.offset) : undefined,
    airportFromId: emptyLeg.airportFrom.id,
    airportToId: emptyLeg.airportTo.id,
    fleetId: emptyLeg.plane.id,
    priceAmount: emptyLeg.price,
    priceCurrencyId: emptyLeg.currency.id,
    hours: emptyLeg.flyHours,
    minutes: emptyLeg.flyMinutes,
    distance: convertFromKm(emptyLeg.flyDistance, userDistanceUnit),
    comment: emptyLeg.comment,
  }

  return (
    <CustomModal
      title={intl.formatMessage(EditEmptyLegModalMessages.title)}
      open={isModalVisible}
      onCancel={() => setIsModalVisible(false)}
      centered
      footer={null}
    >
      <Form
        form={form}
        onFinish={(values) => onFinishTrimmed(values, onFinish)}
        onFinishFailed={onFinishFailed}
        layout={'vertical'}
        initialValues={initialValues}
        onValuesChange={async (changedValues) => {
          handleFormValuesChange(changedValues);
        }}
      >
        <Col flex={1}>
          <CustomFieldInput
            name="dateTypeRadio"
            style={{marginBottom: 8}}
          >
            <Radio.Group>
              <Radio value={EmptyLegDateTypeEnum.RANGE}>{emptyLegDateTypeNameRange}</Radio>
              <Radio
                value={EmptyLegDateTypeEnum.SPECIFIC}>{emptyLegDateTypeNameSpecific}</Radio>
            </Radio.Group>
          </CustomFieldInput>
        </Col>

        <Col flex={1}>
          {dateTypeRadioValue === EmptyLegDateTypeEnum.RANGE &&
            <CustomFieldInput
              label={`${intl.formatMessage(CommonFormMessages.availableDatesLabel)} (UTC)`}
              name={'availableDates'}
              rules={[{required: true, message: intl.formatMessage(CommonFormMessages.ruleRequired)}]}
            >
              <DateAndTimeRangePicker
                format="DD.MM.YYYY"
                disabledDate={isDateLessTanOneMinuteAgo}
                placeholder={[intl.formatMessage(CommonUnitMessages.from), intl.formatMessage(CommonUnitMessages.to)]}
                inputReadOnly={true} placement={"bottomLeft"}
                getPopupContainer={trigger => trigger.parentNode as HTMLElement}
                airportFromOffset={emptyLeg.airportFrom?.timezone?.offset ?? null}
                airportToOffset={emptyLeg.airportTo?.timezone?.offset ?? null}
              />
            </CustomFieldInput>
          }

          {dateTypeRadioValue === EmptyLegDateTypeEnum.SPECIFIC &&
            <CustomFieldInput
              label={`${intl.formatMessage(CommonFormMessages.departureDateLabel)} (UTC)`}
              name={'departureDate'}
              rules={getDateAndTimePickerValidationRules(intl, true)}>
              <DateAndTimePicker
                format="YYYY-MM-DD"
                placeholder={intl.formatMessage(CommonFormMessages.departureDatePlaceholder)}
                disabledDate={isDateLessTanOneMinuteAgo}
                inputReadOnly={true} placement={"bottomLeft"}
                getPopupContainer={trigger => trigger.parentNode as HTMLElement}
              />
            </CustomFieldInput>
          }
        </Col>

        <Row style={{marginBottom: 16}}>
          <Col span={24}>
            <CustomSwitch labelFirst
              checked={isPriceOnRequest}
              onChange={(checked) => {
                setIsPriceOnRequest(checked);
                if (checked) {
                  // Clear price and currency fields when "Price on request" is enabled
                  form.setFieldsValue({
                    priceAmount: undefined,
                    priceCurrencyId: undefined,
                  });
                }
              }}
              label={intl.formatMessage(CommonFormMessages.priceOnRequestLabel)}
            />
          </Col>
        </Row>

        <Row gutter={8}>
          <Col span={12}>
            <CustomFieldInput
              label={intl.formatMessage(CommonFormMessages.priceLabel)}
              name={'priceAmount'}
              rules={[
                {required: !isPriceOnRequest, message: intl.formatMessage(CommonFormMessages.ruleRequired)},
                {
                  min: 0,
                  type: "number",
                  message: intl.formatMessage(CommonFormMessages.ruleMinimumZero)
                },
                {
                  max: 1000000000,
                  type: "number",
                  message: intl.formatMessage(CommonFormMessages.ruleHugeNumber)
                }
              ]}>
              <CustomInputNumber 
                controls={false} 
                placeholder={intl.formatMessage(CommonFormMessages.pricePlaceholder)} 
                style={{width: '100%'}}
                disabled={isPriceOnRequest}
              />
            </CustomFieldInput>
          </Col>
          <Col span={12}>
            <CustomFieldInput
              label={intl.formatMessage(CommonFormMessages.currencyLabel)}
              name={'priceCurrencyId'}
              rules={[{required: !isPriceOnRequest, message: intl.formatMessage(CommonFormMessages.ruleRequired)}]}
            >
              <DebounceSelect
                fetchOptions={fetchCurrencies}
                placeholder={intl.formatMessage(CommonFormMessages.currencyPlaceholder)}
                defaultOptions={[getInitialCurrencyForSelect(emptyLeg.currency)]}
                disabled={isPriceOnRequest}
              />
            </CustomFieldInput>
          </Col>
        </Row>

        <Form.Item>
          <CustomButton
            block
            heightSize={"l"}
            type={'primary'}
            htmlType={'submit'}
            loading={isLoading}
          >
            {intl.formatMessage(CommonButtonMessages.edit)}
          </CustomButton>
        </Form.Item>
      </Form>
    </CustomModal>
  );
};
